<template>
  <app-modal v-model="isDialogOpened"
             size="md"
             :ok-text="$t('subscriptions.views.SubscriptionConsumption.ShareDialog.share')"
             :ok-loading="okLoading"
             @cancel="closeDialog"
             @ok="submitForm"
  >
    <template #title>{{ title }}</template>

    <template #body>
      <div>
        <div class="mb-6">
          <p class="heading-xs-bold text-text-primary">
            {{ $t('subscriptions.views.SubscriptionConsumption.ShareDialog.emailAddresses') }}
          </p>
          <v-combobox v-model="emailAddresses"
                      class="share-text-field"
                      multiple
                      clearable
                      density="compact"
                      variant="outlined"
                      color="blue-darken-2"
                      :clear-on-select="false"
                      :placeholder="$t('subscriptions.views.SubscriptionConsumption.ShareDialog.emailAddressesPlaceholder')"
                      @update:search="onEmailAddressesInputChange"
                      @update:modelValue="validateEmails"
                      :error-messages="emailAddressesErrors"
                      @blur="v$.emailAddressesInput.$touch"
                      ref="emailAddressesCombobox"
          >
            <template v-slot:selection="data">
              <v-chip
                closable
                @click:close="removeEmailAddress(data.index)"
                size="small"
              >
                <template v-slot:prepend>
                  <app-material-icon name="mail" class="mr-1" />
                </template>
                {{ data.item.title }}
              </v-chip>
            </template>

            <template v-if="emailAddressesInput" #append-item>
              <div class="px-5 py-2 d-flex align-center cursor-pointer inputPreview column-gap-2" @click="addEmailAddress">
                <app-material-icon name="mail" color="grey-600" />
                <div class="text-grey-900">{{ emailAddressesInput }}</div>
              </div>
            </template>
          </v-combobox>
        </div>

        <div class="mb-6">
          <p class="heading-xs-bold text-text-primary">
            {{ $t('subscriptions.views.SubscriptionConsumption.ShareDialog.customMessage') }}
          </p>
          <v-textarea
            v-model="customMessage"
            class="share-text-field"
            color="blue-darken-2"
            counter="200"
            persistent-counter
            variant="outlined"
            :placeholder="$t('subscriptions.views.SubscriptionConsumption.ShareDialog.customMessagePlaceholder')"
            rows="4"
            :error-messages="customMessageErrors"
          />
        </div>
      </div>
    </template>
  </app-modal>
</template>

<script>
import {useVuelidate} from "@vuelidate/core";
import { helpers, maxLength, required} from "@vuelidate/validators";

import {formFieldErrors} from "@/common/utils/form";
import { toast } from '@/common/utils/toast'
import { dialogMixin } from '@/mixins/dialogMixin'
import { postmarkEmail } from '@/validators/postmarkEmail'

export default {
  name: 'ShareSubscriptionConsumptionDialog',
  mixins: [dialogMixin],
  setup: () => ({ v$: useVuelidate() }),
  props: {
    title: {
      type: String,
      required: true,
    },
    submitAction: {
      type: Function,
      required: true,
    },
    actionParams: {
      type: Object,
      default: () => ({}),
    },
    okLoading: {
      type: Boolean,
      required: true,
    },
  },
  data () {
    return {
      emailAddresses: [],
      emailAddressesInput: '',
      customMessage: '',
    }
  },
  validations() {
    return {
      emailAddresses: {
        required: required,
        $each: helpers.forEach({
          postmarkEmail,
        }),
      },
      emailAddressesInput: {
        postmarkEmail,
      },
      customMessage: {
        maxLengthValue: maxLength(200),
      },
    }
  },
  computed: {
    emailAddressesErrors() {
      const requiredConstraintErrors = formFieldErrors(
        this.v$.emailAddresses,
        [
          [
            'required',
            this.$t('subscriptions.views.SubscriptionConsumption.ShareDialog.noEmailAddressError'),
          ],
        ],
      )

      const emailInputErrors =  formFieldErrors(
        this.v$.emailAddressesInput,
        [
          [
            'postmarkEmail',
            this.$t('subscriptions.views.SubscriptionConsumption.ShareDialog.invalidEmail'),
          ],
        ],
      )

      return [...requiredConstraintErrors, ...emailInputErrors]
    },
    customMessageErrors () {
      return formFieldErrors(
        this.v$.customMessage,
        [
          [
            'maxLengthValue',
            this.$t('common.validations.textTooLong'),
          ],
        ],
      )
    },
  },
  methods: {
    validateEmails(inputEmails) {
      const addedEmail = inputEmails[inputEmails.length - 1]
      if (!postmarkEmail(addedEmail)) {
        inputEmails.pop()
      } else {
        this.$refs.emailAddressesCombobox.search = ''
      }
    },
    removeEmailAddress(index) {
      this.emailAddresses.splice(index, 1)
    },
    onEmailAddressesInputChange (newInput) {
      this.emailAddressesInput = newInput
    },
    addEmailAddress () {
      if (!this.emailAddressesInput) {
        return
      }

      if (!postmarkEmail(this.emailAddressesInput)) {
        this.v$.emailAddressesInput.$touch()
        return
      }

      this.emailAddresses.push(this.emailAddressesInput)

      this.$refs.emailAddressesCombobox.search = ''
    },
    async submitForm () {
      this.v$.$touch()
      if (!this.v$.$invalid) {

        try {
          await this.submitAction({
            ...this.actionParams,
            data: {
              emailAddresses: this.emailAddresses,
              customMessage: this.customMessage,
            },
          })

          this.closeDialog()
          toast.success(this.$t('subscriptions.views.SubscriptionConsumption.ShareDialog.successMessage'))
        } catch (e) {
          console.error(e)
          toast.error(this.$t('subscriptions.views.SubscriptionConsumption.ShareDialog.errorMessage'))
        }
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

.inputPreview:hover {
  background-color: rgb(var(--v-theme-grey-50));
}

.share-text-field {
  :deep(.v-field) {
    --outline-color: rgb(var(--v-theme-border-secondary));
    --outline-width: #{get-width('border-width-1')};
    --text-color: rgb(var(--v-theme-text-secondary));
    --background-color: rgb(var(--v-theme-surface-primary));
    --v-disabled-opacity: 1;
    --placeholder-color: rgb(var(--v-theme-text-placeholder));
    color: var(--text-color);
    background-color: var(--background-color);

    &:not(.v-field--disabled):hover {
      --outline-color: rgb(var(--v-theme-border-secondary-hover));
    }

    &:not(.v-field--disabled):focus-within {
      --outline-color: rgb(var(--v-theme-border-info-bold));
      --outline-width: #{get-width('border-width-2')};
    }

    &.v-field--disabled {
      --text-color: rgb(var(--v-theme-text-disabled));
      --background-color: rgb(var(--v-theme-surface-disabled));
      --outline-color: rgb(var(--v-theme-border-disabled));
      --placeholder-color: rgb(var(--v-theme-text-disabled));
    }

    &.v-field--error {
      --outline-color: rgb(var(--v-theme-border-danger-bold)) !important;
    }

    .v-field-label:not(.v-field-label--floating) {
      color: var(--placeholder-color);
    }

    .v-field-label--floating {
      color: rgb(var(--v-theme-text-primary))
    }
  }

  :deep(.v-field__input) {
    opacity: 1;
  }

  :deep(.v-field__input input::placeholder) {
    color: var(--placeholder-color) !important;
    opacity: 1;
  }

  :deep(.v-field__input::placeholder) {
    color: var(--placeholder-color) !important;
    opacity: 1;
  }

  :deep(.v-field__outline__start),
  :deep(.v-field__outline__end) {
    border-color: var(--outline-color);
    border-top-width: var(--outline-width);
    border-bottom-width: var(--outline-width);
    opacity: 1;
  }

  :deep(.v-field__outline__notch::before) {
    border-color: var(--outline-color);
    border-top-width: var(--outline-width);
    opacity: 1;
  }

  :deep(.v-field__outline__notch::after) {
    border-color: var(--outline-color);
    border-bottom-width: var(--outline-width);
    opacity: 1;
  }

  :deep(.v-field__outline__start) {
    border-left-width: var(--outline-width);
  }

  :deep(.v-field__outline__end) {
    border-right-width: var(--outline-width);
  }

  &.v-input--error :deep(.v-messages) {
    color: rgb(var(--v-theme-text-danger)) !important;
  }
}
</style>
